import React, { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { getSocket } from "../../lib/socket";
import { BUCKET_KEYS, BUCKET_META, bucketFor, moveForBucket, formatDeadlineBadge } from "../../lib/dateBuckets";
import { FUNCTIONAL_ROLE_COLORS, FUNCTIONAL_ROLE_LABELS } from "./df-constants";

const PRIORITY_BORDER = {
  low: "border-l-ink-300",
  normal: "border-l-blue-400",
  high: "border-l-amber-400",
  urgent: "border-l-red-400",
};

function deadlineBadgeColor(task, bucket) {
  if (bucket === "overdue") return "bg-red-100 text-red-700 border-red-200";
  if (bucket === "today") return "bg-amber-100 text-amber-800 border-amber-200";
  if (bucket === "this_week") return "bg-blue-100 text-blue-700 border-blue-200";
  if (bucket === "next_week") return "bg-indigo-100 text-indigo-700 border-indigo-200";
  if (bucket === "later") return "bg-ink-100 text-ink-600 border-ink-200";
  if (bucket === "completed") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  return "bg-ink-100 text-ink-600 border-ink-200";
}

function checklistTotals(task) {
  const total = (task.checklistStats?.total) ?? (task.checklist?.length ?? 0);
  const done = (task.checklistStats?.done) ?? ((task.checklist || []).filter((c) => c.done).length);
  return { total, done };
}

function TaskCard({ task, dragging, onClick }) {
  const totals = checklistTotals(task);
  const pct = totals.total ? Math.round((totals.done / totals.total) * 100) : 0;
  const bucket = bucketFor(task);
  const dl = formatDeadlineBadge(task.dueDate);
  return (
    <div
      onClick={onClick}
      className={[
        "bg-white rounded-md border-l-[3px] p-3 cursor-pointer transition select-none",
        PRIORITY_BORDER[task.priority] || "border-l-ink-300",
        dragging ? "shadow-xl rotate-1 scale-[1.02] ring-2 ring-blue-200" : "shadow-sm hover:shadow-md hover:-translate-y-0.5",
      ].join(" ")}
      style={{ transition: "all 120ms" }}
    >
      <div className="text-sm font-semibold text-ink-800 line-clamp-2 mb-2">{task.title}</div>

      {(task.projectId || task.department) && (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {task.projectId && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded text-white"
              style={{ background: task.projectId.color || "#94A3B8" }}>
              {task.projectId.name}
            </span>
          )}
          {task.department && (
            <span className="inline-flex items-center gap-1 text-[10px] text-ink-500">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: FUNCTIONAL_ROLE_COLORS[task.department] || "#94A3B8" }} />
              {FUNCTIONAL_ROLE_LABELS[task.department] || task.department}
            </span>
          )}
        </div>
      )}

      {totals.total > 0 && (
        <div className="mb-2">
          <div className="flex justify-between text-[10px] text-ink-500 mb-0.5">
            <span>{totals.done}/{totals.total} checklist</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-1 bg-ink-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        {dl ? (
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded-full border font-medium ${deadlineBadgeColor(task, bucket)}`}>
            🕐 {dl}
          </span>
        ) : <span className="text-[10px] text-ink-400">No deadline</span>}

        <div className="flex items-center gap-1.5 text-[10px] text-ink-500">
          {task.priority !== "normal" && (
            <span className={[
              "px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize",
              task.priority === "urgent" ? "bg-red-100 text-red-700" :
              task.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-ink-100 text-ink-600",
            ].join(" ")}>{task.priority}</span>
          )}
          {(task.comments || []).length > 0 && <span>💬 {task.comments.length}</span>}
          <div className="flex -space-x-2 ml-1">
            {(task.assignees || []).slice(0, 3).map((a) => (
              <div
                key={a._id || a}
                className="w-5 h-5 rounded-full bg-ink-300 text-white text-[9px] font-semibold flex items-center justify-center border-2 border-white"
                title={a.username || ""}
              >
                {(a.username || "?").charAt(0).toUpperCase()}
              </div>
            ))}
            {(task.assignees || []).length > 3 && (
              <div className="w-5 h-5 rounded-full bg-ink-100 text-ink-600 text-[8px] font-semibold flex items-center justify-center border-2 border-white">
                +{task.assignees.length - 3}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ColumnHeader({ bucketKey, count }) {
  const meta = BUCKET_META[bucketKey];
  return (
    <div className="flex items-center justify-between mb-2 px-1">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: meta.color }}>
          {meta.label}
        </span>
        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-ink-100 text-ink-600 font-mono">{count}</span>
      </div>
    </div>
  );
}

export default function DeadlineBoard({ tasks, members = [], onOpenTask, onTasksChange }) {
  const toast = useToast();
  const [collapsedCompleted, setCollapsedCompleted] = useState(true);

  const grouped = useMemo(() => {
    const now = new Date();
    const out = Object.fromEntries(BUCKET_KEYS.map((k) => [k, []]));
    for (const t of (tasks || [])) {
      if (t.parentTaskId) continue; // hide sub-tasks; they appear inside parent panel
      out[bucketFor(t, now)].push(t);
    }
    // Sort within column: overdue/today by oldest deadline first; later by deadline asc; completed by completedAt desc
    out.overdue.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    out.today.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    out.this_week.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    out.next_week.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    out.later.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    out.no_deadline.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    out.completed.sort((a, b) => new Date(b.completedAt || b.updatedAt) - new Date(a.completedAt || a.updatedAt));
    return out;
  }, [tasks]);

  // Real-time merge from socket
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    function onTask({ task }) {
      if (!task) return;
      onTasksChange && onTasksChange((prev) => {
        const list = prev || [];
        const idx = list.findIndex((t) => String(t._id) === String(task._id));
        if (idx === -1) return [task, ...list];
        const next = list.slice();
        next[idx] = { ...list[idx], ...task };
        return next;
      });
    }
    function onTaskDelete({ task }) {
      if (!task) return;
      onTasksChange && onTasksChange((prev) => (prev || []).filter((t) => String(t._id) !== String(task._id)));
    }
    socket.on("task:create", onTask);
    socket.on("task:update", onTask);
    socket.on("task:delete", onTaskDelete);
    return () => {
      socket.off("task:create", onTask);
      socket.off("task:update", onTask);
      socket.off("task:delete", onTaskDelete);
    };
  }, [onTasksChange]);

  async function handleDragEnd(result) {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    const targetBucket = destination.droppableId;
    const task = (tasks || []).find((t) => String(t._id) === String(draggableId));
    if (!task) return;
    const move = moveForBucket(targetBucket, task);
    if (!move) {
      toast({ title: "Can't drop into Overdue — set a past deadline manually.", status: "warning", duration: 2000 });
      return;
    }

    // Optimistic update
    const previousTasks = tasks;
    const next = (tasks || []).map((t) => String(t._id) === String(draggableId) ? { ...t, ...move, ...(move.status === "done" ? { completedAt: new Date().toISOString() } : {}) } : t);
    onTasksChange && onTasksChange(next);

    try {
      const body = {};
      if ("dueDate" in move) body.dueDate = move.dueDate;
      if ("status" in move) {
        body.completed = move.status === "done";
      }
      await api(`/tasks/${draggableId}`, { method: "PATCH", body: JSON.stringify(body) });
    } catch (e) {
      toast({ title: e.message || "Couldn't move task", status: "error" });
      onTasksChange && onTasksChange(previousTasks);
    }
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 280px)" }}>
        {BUCKET_KEYS.map((key) => {
          const list = grouped[key];
          const meta = BUCKET_META[key];
          const isCompletedCollapsed = key === "completed" && collapsedCompleted;
          return (
            <div
              key={key}
              className="flex flex-col"
              style={{ minWidth: isCompletedCollapsed ? "60px" : "280px", width: isCompletedCollapsed ? "60px" : "280px" }}
            >
              {key === "completed" ? (
                <div className="flex items-center justify-between mb-2 px-1">
                  <button
                    onClick={() => setCollapsedCompleted((s) => !s)}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider hover:underline"
                    style={{ color: meta.color }}
                    title={isCompletedCollapsed ? "Expand completed" : "Collapse completed"}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                    {!isCompletedCollapsed && <span>{meta.label}</span>}
                    <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-ink-100 text-ink-600 font-mono">{list.length}</span>
                  </button>
                </div>
              ) : (
                <ColumnHeader bucketKey={key} count={list.length} />
              )}
              <Droppable droppableId={key}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={[
                      "flex-1 rounded-md p-2 transition-colors",
                      snapshot.isDraggingOver ? "bg-blue-50/70" : "bg-ink-50/40",
                    ].join(" ")}
                    style={{ minHeight: "60px" }}
                  >
                    {!isCompletedCollapsed && list.map((t, idx) => (
                      <Draggable key={t._id} draggableId={String(t._id)} index={idx}>
                        {(prov, snap) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            className="mb-2"
                          >
                            <TaskCard task={t} dragging={snap.isDragging} onClick={() => !snap.isDragging && onOpenTask && onOpenTask(t._id)} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {!isCompletedCollapsed && list.length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-center text-[11px] text-ink-300 italic py-3">{meta.help}</div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
